import { loadEcsTreatments } from '@ms/yammer-data/dist/domains/ecs/actions';
import { addEtagToAnalyticsEvent } from '@ms/yammer-data/dist/domains/ecs/addEtagToAnalyticsEvent';
import { addEtagToAnalyticsV2Event } from '@ms/yammer-data/dist/domains/ecs/addEtagToAnalyticsV2Event';
import { getPersistedExperimentTreatment } from '@ms/yammer-data/dist/domains/experiments/selectors';
import { addIsBackgroundLoadToTelemetryEvent } from '@ms/yammer-data/dist/domains/isBackgroundLoad';
import { setPersistKey } from '@ms/yammer-data/dist/domains/persistConfig/actions';
import { sessionBootstrapFailed } from '@ms/yammer-data/dist/domains/sessionBootstrap/actions';
import { configure } from '@ms/yammer-data/dist/domains/sessionConfigure/actions';
import { loadCurrentUser } from '@ms/yammer-data/dist/domains/sessionLoadCurrentUser/actions';
import { configureThemeIsDefault } from '@ms/yammer-data/dist/domains/suiteHeaderTheme/actions';
import { setTelemetryUserContext } from '@ms/yammer-data/dist/domains/telemetry/actions';
import type { ThunkDispatch } from '@ms/yammer-data/dist/types';
import { IndexedDbCache, LocalStorageCache, setCache } from '@ms/yammer-libs-cache';
import {
  isDogfoodPerfHUDPersistEnabled,
  loadLogEventInDogfoodPerfHUDStoreMiddleware,
} from '@ms/yammer-page-route-telemetry';
import type { TelemetryMiddleware } from '@ms/yammer-telemetry';
import {
  acquireTokenWithTelemetry,
  addMiddleware,
  addUrlRootPathToEventMiddleware,
  getErrorMessage,
  reportError,
} from '@ms/yammer-telemetry';
import { AcquireTokenTimedoutError } from '@ms/yammer-web-authenticators';
import { checkThirdPartyCookieSupport } from '@ms/yammer-web-support/dist/cookies';
import { acquireTokenTimeoutInMilliseconds } from 'bootstrap/auth';
import config from 'bootstrap/config';
import { writeCookies } from 'bootstrap/cookies';
import { addPerfTimingsToEvent, loadContext, setLocaleFromTeams, tryHandleDeepLink } from 'domains/teams/actions';
import { getCurrentUserApiCaching, getFeatureFlightingApiCaching } from 'domains/teams/apiCaching';
import { getHostClientType, getTeamsContext } from 'state/teamsContext/selectors';
import type { YammerTeamsNativeState } from 'state/types';

import { persistConfig } from '../../../../persistedState';

type ActionDispatcher = (dispatch: ThunkDispatch<YammerTeamsNativeState>) => void;
type ActionDispatcherWithError = (dispatch: ThunkDispatch<YammerTeamsNativeState>, error: unknown) => void;
type AsyncActionDispatcher = (dispatch: ThunkDispatch<YammerTeamsNativeState>) => Promise<void>;
type AsyncActionDispatcherWithState = (
  dispatch: ThunkDispatch<YammerTeamsNativeState>,
  state: YammerTeamsNativeState
) => Promise<void>;

type SetupCache = () => Promise<void>;
export const setupCache: SetupCache = async () => {
  const teamsContext = getTeamsContext();
  if (!teamsContext?.userId) {
    throw new Error('User context is missing, cannot set up cache.');
  }
  const userId = teamsContext.userId;
  const keyNamespace = `teams-branded-exp::${userId}`;

  const isIdbCacheEnabled = !!(await getPersistedExperimentTreatment(
    persistConfig.key,
    'TeamsBrandedExperiencesIdbCache'
  ));
  const cache = isIdbCacheEnabled ? new IndexedDbCache(keyNamespace) : new LocalStorageCache(keyNamespace);
  setCache(cache);
};

export const acquireYammerTokenWithTelemetry: (silent?: boolean) => Promise<string> = async (silent) => {
  try {
    return acquireTokenWithTelemetry({
      scopes: [config.adal.resources.yammer],
      timeout: acquireTokenTimeoutInMilliseconds,
      silent,
      initiator: 'bootstrap',
    });
  } catch (error: unknown) {
    if (error instanceof AcquireTokenTimedoutError) {
      reportError({ error, eventProperties: { errorCode: 'AcquireTokenTimedout' } });
    } else {
      reportError({ error });
    }

    throw error;
  }
};

export const writeHostingCookies: (yammerToken: string) => Promise<void> = async (yammerToken) => {
  try {
    const partitioned = !checkThirdPartyCookieSupport();

    writeCookies(yammerToken, partitioned);
  } catch (error: unknown) {
    reportError({ error, eventProperties: { errorCode: 'WriteHostingCookiesFailed' } });
    throw error;
  }
};

export const setupFeatureFlighting: AsyncActionDispatcherWithState = async (dispatch) => {
  try {
    const teamsContext = getTeamsContext();
    const userContext =
      teamsContext?.userId && teamsContext.tenantId
        ? {
            userId: teamsContext.userId,
            tenantId: teamsContext.tenantId,
          }
        : undefined;

    if (!userContext) {
      reportError({
        error: 'Missing Teams user context for ECS call',
        eventProperties: { errorCode: 'FeatureFlightingFallingBackToAuthTokenCredentials' },
      });
    }

    await dispatch(
      loadEcsTreatments({
        caching: getFeatureFlightingApiCaching(!!teamsContext?.isBackgroundLoad),
        initiator: 'bootstrap',
        persistKey: persistConfig.key,
        userContext,
      })
    );
  } catch (error: unknown) {
    reportError({ error, eventProperties: { errorCode: 'InitializeFeatureFlightingFailed' } });
  }
};

export const loadUserData: AsyncActionDispatcherWithState = async (dispatch) => {
  try {
    const caching = getCurrentUserApiCaching('ReadCacheOrFetchAndWrite', getHostClientType());
    await dispatch(loadCurrentUser({ caching, initiator: 'bootstrap' }));
    await dispatch(setTelemetryUserContext());
  } catch (error: unknown) {
    reportError({ error, eventProperties: { errorCode: 'LoadCurrentUserFailed' } });
    throw error;
  }
};

export const configureSession: ActionDispatcher = (dispatch) => {
  dispatch(configure(config));
};

export const savePersistKey: ActionDispatcher = (dispatch) => {
  dispatch(setPersistKey(persistConfig.key));
};

type AddTelemetryMiddleware = (getState: () => YammerTeamsNativeState) => TelemetryMiddleware;
type AddStatelessTelemetryMiddleware = () => TelemetryMiddleware;

const getAddEtagToAnalyticsEventMiddleware: AddTelemetryMiddleware = (getState) => (event) =>
  addEtagToAnalyticsEvent(event, getState);

const getAddEtagToAnalyticsV2EventMiddleware: AddTelemetryMiddleware = (getState) => (event) =>
  addEtagToAnalyticsV2Event(event, getState);

const getAddPerfTimingsMiddleware: AddTelemetryMiddleware = (getState) => async (event) =>
  addPerfTimingsToEvent(event, getState);

const getAddIsBackgroundLoadMiddleware: AddStatelessTelemetryMiddleware = () => (event) =>
  addIsBackgroundLoadToTelemetryEvent(event);

export const addTelemetryMiddleware: (getState: () => YammerTeamsNativeState) => Promise<void> = async (getState) => {
  const etagMiddleware = getAddEtagToAnalyticsEventMiddleware(getState);
  const etagV2Middleware = getAddEtagToAnalyticsV2EventMiddleware(getState);
  const perfTimingsMiddleware = getAddPerfTimingsMiddleware(getState);
  const isBackgroundLoadMiddleware = getAddIsBackgroundLoadMiddleware();

  addMiddleware(etagMiddleware);
  addMiddleware(etagV2Middleware);
  addMiddleware(perfTimingsMiddleware);
  addMiddleware(addUrlRootPathToEventMiddleware);
  addMiddleware(isBackgroundLoadMiddleware);

  const isPerfHUDEnabled = await isDogfoodPerfHUDPersistEnabled(persistConfig.key);
  if (isPerfHUDEnabled) {
    const { logEventInDogfoodPerfHUDStoreMiddleware } = await loadLogEventInDogfoodPerfHUDStoreMiddleware();
    addMiddleware(logEventInDogfoodPerfHUDStoreMiddleware);
  }
};

export const setupUserLocale: AsyncActionDispatcher = async (dispatch) => {
  await dispatch(setLocaleFromTeams());
};

export const loadTeamsContext = async (): Promise<void> => {
  await loadContext();
  tryHandleDeepLink(false);
};

export const storeBootstrapFailure: ActionDispatcherWithError = (dispatch, error) => {
  const errorObject = error instanceof Error ? error : new Error(getErrorMessage(error));
  dispatch(sessionBootstrapFailed(errorObject));
};

export const configureThemeSettings: ActionDispatcher = (dispatch) => {
  dispatch(configureThemeIsDefault());
};
